import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Slider, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRemove } from '@fortawesome/free-solid-svg-icons';

import { useAppSelector } from 'srs.sharedcomponents/lib/esm/redux/hooks';
import { IItemListResponse } from 'srs.sharedcomponents/lib/esm/models/responses/srs.itemListResponse.model';
import {
    loading,
    selectSearchValue,
    selectVehicleApplicationClicked,
    updateisMultiSelectFiltersApplied,
    updateMultiSelectFilters,
    clearFilters,
    selectSelectedFilters,
    selectApiPayload,
    updateFilters as updateFiltersRedux,
} from 'srs.sharedcomponents/lib/esm/redux/slices/productlistSlice';
import { useTranslate } from 'srs.sharedcomponents/lib/esm/hooks/useTranslate';

import { useSliderItemsData } from './multiSelectComponents/priceSliderData/sliderItemsData';
import {
    SelectedValues,
    updateFilters,
    VehicleData,
} from './AdditionalFiltersMenuComponent';
import { extractFilterData } from './FiltersDataExtraction';
import { getFilteredData } from './MultiSelectDataHandling';
import RenderItems from './multiSelectComponents/SelectedItemsList/SelectedItemsList';
import { facetTrimingCatalogValidator } from 'Utils/facetTrimingCatalogValidator';
import { SubFiltersMenu } from './multiSelectComponents/SubFiltersMenu/SubFiltersMenu';

interface ISelectedFilter {
    make: string[];
    year: string[];
    model: string[];
    component: string[];
    supplier: string[];
    category: string[];
    isOnSaleClicked: boolean;
    isNewClicked: boolean;
    isCallForPricingClicked: boolean;
    minPrice: number;
    maxPrice: number;
}

const MultiSelectList = ({
    totalSearchItems,
    updatedFilterItems,
}: {
    totalSearchItems: IItemListResponse | undefined;
    updatedFilterItems: IItemListResponse | undefined;
}) => {
    type searchedValue = {
        make: '';
        year: '';
        model: '';
        component: '';
        supplier: '';
        category: '';
    };

    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const recentSearchedValue = useAppSelector(selectSearchValue);
    const {
        makes,
        allYears,
        allModels,
        allComponents,
        allCategoriesWithChildren,
        allInitialCategoriesWithChildren,
        allSuppliers,
    } = extractFilterData(updatedFilterItems, totalSearchItems);
    const isVehicleApplicationClicked = useAppSelector(
        selectVehicleApplicationClicked
    );
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const filtersStored = useAppSelector(selectSelectedFilters);
    const apiPayloadStored = useAppSelector(selectApiPayload);
    const searchValue = searchParams.get('search');
    const categoryValue = searchParams.get('isCategory');
    const tagValue = searchParams.get('TAG');
    const isLoading: boolean = useAppSelector(loading);

    let subSearch = searchParams.get('subSearch');

    const [clearButtonClicked, setClearButtonClicked] =
        useState<boolean>(false);
    const [isPriceRangeSelected, setIsPriceRangeSelected] =
        useState<boolean>(true);
    const [isPriceRangeMoved, setIsPriceRangeMoved] = useState<boolean>(false);
    const [priceRangeValues, setPriceRangeValues] = useState<any>([
        -1, 1000000,
    ]);
    const [isApplyButtonVisible, setIsApplyButtonVisible] = useState(false);
    const history = useHistory();
    const [previousLocation, setPreviousLocation] = useState(history.location);
    const [isOnSaleClicked, setIsOnSaleClicked] = useState<boolean>(false);
    const [isOnSaleDisabled, setIsOnSaleDisabled] = useState<boolean>(false);
    const [isNewDisabled, setIsNewDisabled] = useState<boolean>(false);
    const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
    const [previousMinPrice, setPreviousMinPrice] = useState<number | null>(
        totalSearchItems?.minPrice ?? null
    );
    const [previousMaxPrice, setPreviousMaxPrice] = useState<number | null>(
        totalSearchItems?.maxPrice ?? null
    );
    const [callForPricingProducts, setCallForPricingProducts] =
        useState<boolean>(false);
    const [isCallForPricingClicked, setIsCallForPricingClicked] =
        useState<boolean>(false);
    const [isInitialPageLoad, setIsInitialPageLoad] = useState(false);
    const [showAllSelectedValues, setShowAllSelectedValues] = useState(false);
    const { minPrice, maxPrice, onSaleProducts, newProducts } =
        useSliderItemsData(
            updatedFilterItems,
            isPriceRangeSelected,
            totalSearchItems,
            setPreviousMaxPrice,
            setPreviousMinPrice,
            previousMaxPrice,
            previousMinPrice
        );
    const [price, setPrice] = useState<number[]>([
        minPrice ?? -1,
        maxPrice ?? 1000000,
    ]);
    const [finalPrice, setFinalPrice] = useState<number[]>([
        priceRangeValues[0] ?? -1,
        priceRangeValues[1] ?? 1000000,
    ]);
    const [displayedMin, setDisplayedMin] = useState(
        Math.max(finalPrice[0], minPrice ?? 0)
    );
    const [displayedMax, setDisplayedMax] = useState(
        Math.min(finalPrice[1], maxPrice ?? 1000000)
    );
    const [selectedValues, setSelectedValues] = useState<SelectedValues>({
        make: [],
        year: [],
        model: [],
        component: [],
        supplier: [],
        category: [],
    });
    const [searchedValueLabel, setSearchedValueLabel] = useState<searchedValue>(
        {
            make: '',
            year: '',
            model: '',
            component: '',
            supplier: '',
            category: '',
        }
    );
    const [data, setData] = useState<VehicleData>({
        make: {},
        model: {},
        year: {},
        component: {},
    });

    // Use Effects
    useEffect(() => {
        const hasArrayFilters =
            filtersStored?.category?.length ||
            filtersStored?.supplier?.length ||
            filtersStored?.make?.length ||
            filtersStored?.year?.length ||
            filtersStored?.model?.length ||
            filtersStored?.component?.length;

        const hasBooleanFilters =
            filtersStored?.isOnSaleClicked ||
            filtersStored?.isNewClicked ||
            filtersStored?.isCallForPricingClicked;

        const hasPriceFilters =
            filtersStored?.minPrice !== 0 ||
            filtersStored?.maxPrice !== 0 ||
            filtersStored?.previousMinPrice != 0 ||
            filtersStored?.previousMaxPrice != 0;

        if (hasArrayFilters || hasBooleanFilters || hasPriceFilters) {
            buildParametersAboutUpdateFiltersBasedOnStoredValues();
        }
    }, []);

    useEffect(() => {
        const handleLocationChange = async (location: any, action: any) => {
            const { pathname, search } = location;
            const isProductListCatalog = pathname === '/ProductListCatalog';
            const isSameSearch = search === previousLocation.search;
            const isActionPush = action === 'PUSH';
            const isActionPop = action === 'POP';

            if (location !== previousLocation) {
                if (
                    isProductListCatalog &&
                    isSameSearch &&
                    (isActionPop || isActionPush)
                ) {
                    await handleNavigationAction(search);
                }
                setPreviousLocation(location);
            }
        };

        const unregisterLocationListener = history.listen(handleLocationChange);
        return () => {
            unregisterLocationListener();
        };
    }, [history, previousLocation]);

    useEffect(() => {
        if (updatedFilterItems?.minPrice == 0) {
            setCallForPricingProducts(true);
        } else {
            setCallForPricingProducts(false);
        }
    }, [updatedFilterItems]);

    useEffect(() => {
        if (
            Object.values(selectedValues).reduce(
                (acc, curr) => acc + curr.length,
                0
            ) === 0 &&
            !isCallForPricingClicked &&
            !isOnSaleClicked &&
            !isNewClicked
        ) {
            setIsPriceRangeSelected(true);
        }
    }, [selectedValues]);

    useEffect(() => {
        if (
            tagValue &&
            tagValue !== 'On Sale' &&
            !categoryValue &&
            !searchValue
        ) {
            storeSupplierSelectedFromSupplierPageSelection(
                totalSearchItems?.minPrice ?? 0,
                totalSearchItems?.maxPrice ?? 0,
                tagValue
            );
        }

        if (!totalSearchItems && isLoading) {
            buildParametersAboutUpdateFiltersBasedOnStoredValues();
        }

        pricesFromStorePreviousPriceSetter(
            totalSearchItems?.minPrice,
            totalSearchItems?.maxPrice
        );
    }, [totalSearchItems]);

    useEffect(() => {
        if (recentSearchedValue !== '') {
            setIsOnSaleClicked(false);
            setIsNewClicked(false);
            setIsCallForPricingClicked(false);
            setIsOnSaleDisabled(false);
            setIsNewDisabled(false);
            setIsApplyButtonVisible(false);
            setPrice([-1, 1000000]);
            setFinalPrice([-1, 1000000]);
            setIsPriceRangeSelected(true);
            setPriceRangeValues([-1, 1000000]);

            if (recentSearchedValue === 'On Sale') {
                setIsOnSaleClicked(true);
                setIsOnSaleDisabled(true);
            } else if (recentSearchedValue === 'NewProducts') {
                setIsNewClicked(true);
                setIsNewDisabled(true);
            }

            setIsPriceRangeMoved(false);
            previousSelectedValues.current = [];
            setIsInitialPageLoad(true);
            pricesFromStorePreviousPriceSetter(
                totalSearchItems?.minPrice,
                totalSearchItems?.maxPrice
            );
        }
    }, [recentSearchedValue]);

    useEffect(() => {
        if (!isLoading) {
            setIsInitialPageLoad(false);
        }
    }, [totalSearchItems, isLoading]);

    useEffect(() => {
        setDisplayedMin(Math.max(finalPrice[0], minPrice ?? 0));
        setDisplayedMax(Math.min(finalPrice[1], maxPrice ?? 1000000));
    }, [finalPrice, minPrice, maxPrice, recentSearchedValue]);

    useEffect(() => {
        if (clearButtonClicked) {
            setPreviousMinPrice(updatedFilterItems?.minPrice ?? null);
            setPreviousMaxPrice(updatedFilterItems?.maxPrice ?? null);
        }
    }, [totalSearchItems, updatedFilterItems]);

    // Functions
    const handleNavigationAction = (locationSearch: string) => {
        return new Promise<void>((resolve) => {
            history.push(`/ProductListCatalog?search=`);
            setTimeout(() => {
                history.push(`/ProductListCatalog${locationSearch}`);
                resolve();
            }, 50);
        });
    };

    const handleSaleClick = () => {
        setIsPriceRangeSelected(false);
        setIsOnSaleClicked(!isOnSaleClicked);
    };

    const handleNewClick = () => {
        setIsPriceRangeSelected(false);
        setIsNewClicked(!isNewClicked);
    };

    const handleCallForPricingClick = () => {
        setIsPriceRangeSelected(false);
        setIsCallForPricingClicked(!isCallForPricingClicked);
    };

    const isSaleActiveFromProducts =
        (updatedFilterItems?.totalCount === 0
            ? isOnSaleClicked
            : onSaleProducts && isOnSaleClicked) && !isOnSaleDisabled;
    const isNewActiveFromProducts =
        (updatedFilterItems?.totalCount === 0
            ? isNewClicked
            : newProducts && isNewClicked) && !isNewDisabled;
    const isCallForPricingProductsActive =
        updatedFilterItems?.totalCount === 0
            ? isCallForPricingClicked
            : callForPricingProducts && isCallForPricingClicked;

    const filterTranslations = [
        {
            condition: isSaleActiveFromProducts,
            translationKey: 'On_Sale_FacetText',
        },
        {
            condition: isNewActiveFromProducts,
            translationKey: 'CMSNewText',
        },
        {
            condition: isCallForPricingProductsActive,
            translationKey: 'MissingPrice',
        },
    ];

    const additionalFilters = filterTranslations
        .filter(({ condition }) => condition)
        .map(({ translationKey }) => translate(translationKey));

    const extractDeepestCodes = (category: {
        label: string;
        items: any;
        code: string;
    }): string[] => {
        if (!category.items || category.items.length === 0) {
            return category.code ? [category.code] : [];
        }

        return category.items.flatMap(extractDeepestCodes);
    };

    const extractAllDeepestCodes = (
        categories: {
            label: string;
            items: any[];
            code: string;
        }[]
    ): string[] => {
        return categories.flatMap(extractDeepestCodes);
    };

    const categoryCodes = extractAllDeepestCodes(allCategoriesWithChildren);

    const formatCategoryCodeForDisplay = (code: string): string | null => {
        for (const topCategory of allCategoriesWithChildren) {
            const isFacetTriming =
                facetTrimingCatalogValidator.isFacetTrimingCatalog(
                    topCategory.code
                );
            if (!isFacetTriming) continue;

            for (const subCategory of topCategory.items || []) {
                // Check leaf items under subcategories (e.g., ESST > Make > Ford)
                if (subCategory.items && subCategory.items.length > 0) {
                    for (const leaf of subCategory.items) {
                        if (leaf.code === code) {
                            return `${topCategory.label} > ${leaf.label}`;
                        }
                    }
                }
                // Check if code matches the subcategory itself
                if (subCategory.code === code) {
                    return `${topCategory.label} > ${subCategory.label}`;
                }
            }
        }
        return null;
    };

    const filterIntersectedValues = (
        items: any[],
        reference: string | any[]
    ): string[] => {
        if (updatedFilterItems?.totalCount === 0) {
            let allItems: string[] = [...items];

            allItems.forEach((code: string, index: number) => {
                const formatted = formatCategoryCodeForDisplay(code);
                if (formatted) {
                    allItems[index] = formatted;
                }
            });

            return allItems;
        }
        let filteredItems: string[] = items.filter((item: any) =>
            reference.includes(item)
        );

        filteredItems.forEach((code: string, index: number) => {
            const formatted = formatCategoryCodeForDisplay(code);
            if (formatted) {
                filteredItems[index] = formatted;
            }
        });

        return filteredItems;
    };

    const allSelectedItems = [
        ...additionalFilters,
        ...filterIntersectedValues(selectedValues.category, categoryCodes),
        ...filterIntersectedValues(selectedValues.supplier, allSuppliers),
        ...filterIntersectedValues(selectedValues.make, makes),
        ...filterIntersectedValues(selectedValues.year, allYears),
        ...filterIntersectedValues(selectedValues.model, allModels),
        ...filterIntersectedValues(selectedValues.component, allComponents),
    ];

    const previousSelectedValues = useRef(allSelectedItems);

    const handleSelectedValueClick = (item: string) => {
        if (item === translate('MissingPrice')) {
            handleCallForPricingClick();
        }
        if (item === translate('CMSNewText')) {
            handleNewClick();
        }
        if (item === translate('On_Sale_FacetText')) {
            handleSaleClick();
        }

        setSelectedValues((prevSelectedValues) => {
            const updatedSelections = updateCategoryValues(
                prevSelectedValues,
                item,
                allCategoriesWithChildren
            );

            updateSelections(
                updatedFilterItems,
                updatedSelections,
                setData,
                makes,
                allYears,
                allModels,
                allComponents
            );

            return updatedSelections;
        });
    };

    const selectedValuesChanged =
        JSON.stringify(previousSelectedValues.current) !==
        JSON.stringify(allSelectedItems);

    const handleApplyFiltersButton = async () => {
        setIsPriceRangeSelected(!selectedValuesChanged);
        if (
            Object.values(selectedValues).reduce(
                (acc, curr) => acc + curr.length,
                0
            ) === 0 &&
            !isCallForPricingClicked &&
            !isOnSaleClicked &&
            !isNewClicked
        ) {
            setIsPriceRangeSelected(true);
            setPreviousMaxPrice(totalSearchItems?.maxPrice ?? null);
            setPreviousMinPrice(totalSearchItems?.minPrice ?? null);
        }
        previousSelectedValues.current = allSelectedItems;

        const searchUrlParam = searchParams.get('search');
        const tagUrlParam = searchParams.get('TAG');
        const searchParam = searchUrlParam ?? tagUrlParam ?? subSearch ?? '';

        sessionStorage.setItem('searchedValue', searchParam);

        await updateFilters(
            {
                dispatchFilters: dispatch,
                onSale: isOnSaleClicked,
                newProduct: isNewClicked,
                priceRange: finalPrice,
                allCategories: allCategoriesWithChildren,
                newSelectedValues: selectedValues,
                callForPricing: isCallForPricingClicked,
                updatedFilterItems: updatedFilterItems,
                isPriceRangeSelected: isPriceRangeSelected,
                priceRangeValues: priceRangeValues,
                searchedLabel: searchedValueLabel,
                setIsInitialPageLoad: setIsInitialPageLoad,
                subSearch: subSearch,
                searchParam: searchParam,
                filtersStored: filtersStored,
                previousMinPrice: previousMinPrice ?? 0,
                previousMaxPrice: previousMaxPrice ?? 0,
                allInitialCategoriesWithChildren:
                    allInitialCategoriesWithChildren,
            },
            !!isVehicleApplicationClicked
        );
    };

    const isSupplierPage = !!(
        tagValue &&
        tagValue !== 'On Sale' &&
        !categoryValue &&
        !searchValue
    );

    const handleClearAllSelectedValues = async () => {
        setSelectedValues({
            make: [],
            year: [],
            model: [],
            component: [],
            supplier: isSupplierPage ? [tagValue] : [],
            category: [],
        });
        setData({
            make: {},
            model: {},
            year: {},
            component: {},
        });
        !isOnSaleDisabled && setIsOnSaleClicked(false);
        !isNewDisabled && setIsNewClicked(false);
        setIsCallForPricingClicked(false);
        dispatch(updateMultiSelectFilters(null));
        dispatch(updateisMultiSelectFiltersApplied(false));
        setShowAllSelectedValues(false);

        previousSelectedValues.current = [];
        additionalFilters.length +
            Object.values(selectedValues).reduce(
                (acc, curr) => acc + curr.length,
                0
            ) +
            (isPriceRangeMoved ? 1 : 0) >
            0 && setIsInitialPageLoad(true);

        handleResetPriceRange();

        sessionStorage.setItem('searchedValue', '');
        dispatch(clearFilters());

        setClearButtonClicked(true);
    };

    const handleToggleClick = () => {
        setShowAllSelectedValues((prevState) => !prevState);
    };

    const handleResetPriceRange = () => {
        setDisplayedMin(minPrice ?? 0);
        setDisplayedMax(maxPrice ?? 1000000);
        setIsPriceRangeMoved(false);
        setFinalPrice([minPrice ?? -1, maxPrice ?? 1000000]);
        setPrice([minPrice ?? -1, maxPrice ?? 1000000]);
        setIsApplyButtonVisible(true);
    };

    const totalSelectedCount =
        allSelectedItems.length + (isPriceRangeMoved ? 1 : 0);

    const pricesFromStorePreviousPriceSetter = (
        totalSearchItemsMinPrice: number | undefined,
        totalSearchItemsMaxPrice: number | undefined
    ) => {
        const isFiltersFromStoreEmpty = storedFilterEmpty(filtersStored);

        let previousMinPriceAux = totalSearchItemsMinPrice ?? null;
        let previousMaxPriceAux = totalSearchItemsMaxPrice ?? null;

        if (!isFiltersFromStoreEmpty && isPriceRangeMoved) {
            previousMinPriceAux = filtersStored?.previousMinPrice ?? null;
            previousMaxPriceAux = filtersStored?.previousMaxPrice ?? null;
        }

        setPreviousMinPrice(previousMinPriceAux);
        setPreviousMaxPrice(previousMaxPriceAux);
    };

    const buildParametersAboutUpdateFiltersBasedOnStoredValues = () => {
        updateFiltersBasedOnStoredValues(
            {
                make: filtersStored?.make ?? [],
                year: filtersStored?.year ?? [],
                model: filtersStored?.model ?? [],
                component: filtersStored?.component ?? [],
                supplier: filtersStored?.supplier ?? [],
                category: filtersStored?.category ?? [],
            },
            filtersStored?.isOnSaleClicked ?? false,
            filtersStored?.isNewClicked ?? false,
            filtersStored?.isCallForPricingClicked ?? false,
            filtersStored?.minPrice ?? 0,
            filtersStored?.maxPrice ?? 0,
            filtersStored?.previousMinPrice ?? 0,
            filtersStored?.previousMaxPrice ?? 0
        );
    };

    const storedFilterEmpty = (filter: ISelectedFilter | undefined) => {
        if (!filter) return;

        return !(
            filter.make.length > 0 ||
            filter.year.length > 0 ||
            filter.model.length > 0 ||
            filter.component.length > 0 ||
            filter.supplier.length > 0 ||
            filter.category.length > 0 ||
            filter.isCallForPricingClicked ||
            filter.isNewClicked ||
            filter.isOnSaleClicked ||
            filter.maxPrice !== 0
        );
    };

    const updateFiltersBasedOnStoredValues = async (
        newSelectedValuesStored: SelectedValues,
        isOnSaleClickedStored: boolean,
        isNewClickedStored: boolean,
        isCallForPricingClickedStored: boolean,
        minPriceStored: number,
        maxPriceStored: number,
        previousMinPriceStored: number,
        previousMaxPriceStored: number
    ) => {
        const storedSearchedValue = sessionStorage.getItem('searchedValue');

        if (
            storedSearchedValue &&
            (storedSearchedValue === searchValue ||
                storedSearchedValue === tagValue)
        ) {
            const isFiltersFromStoreEmpty = storedFilterEmpty(filtersStored);

            if (isFiltersFromStoreEmpty) {
                setPriceRangeValues([finalPrice[0], finalPrice[1]]);

                return;
            }

            await dispatch(updateisMultiSelectFiltersApplied(false));
            await dispatch(updateMultiSelectFilters(null));

            setSelectedValues(newSelectedValuesStored);
            setIsOnSaleClicked(isOnSaleClickedStored);
            setIsNewClicked(isNewClickedStored);
            setIsCallForPricingClicked(isCallForPricingClickedStored);
            setIsApplyButtonVisible(true);

            if (minPriceStored !== 0 && maxPriceStored !== 0) {
                setPrice([minPriceStored, maxPriceStored]);
                setPriceRangeValues([minPriceStored, maxPriceStored]);
                setFinalPrice([minPriceStored, maxPriceStored]);
                setIsPriceRangeMoved(true);
                setIsPriceRangeSelected(true);
                setDisplayedMin(minPriceStored);
                setDisplayedMax(maxPriceStored);
                setPreviousMinPrice(previousMinPriceStored);
                setPreviousMaxPrice(previousMaxPriceStored);
            }

            dispatch(updateMultiSelectFilters(apiPayloadStored));
            dispatch(updateisMultiSelectFiltersApplied(true));
        } else {
            dispatch(clearFilters());
        }
    };

    const storeSupplierSelectedFromSupplierPageSelection = (
        totalSearchMinPrice: number,
        totalSearchMaxPrice: number,
        supplierSelected: string
    ) => {
        const storedSearchedValue = sessionStorage.getItem('searchedValue');

        setSelectedValues((prev) => ({
            ...prev,
            supplier: [supplierSelected],
        }));

        if (
            !storedSearchedValue ||
            storedSearchedValue === '' ||
            storedSearchedValue !== supplierSelected
        ) {
            const updatedMultiSelectedFilters = {
                searchedValue: supplierSelected,
                selectedFilters: {
                    make: [],
                    year: [],
                    model: [],
                    component: [],
                    supplier: [supplierSelected],
                    category: [],
                    isOnSaleClicked: false,
                    isNewClicked: false,
                    isCallForPricingClicked: false,
                    minPrice: totalSearchMinPrice,
                    maxPrice: totalSearchMaxPrice,
                    previousMinPrice: totalSearchMinPrice,
                    previousMaxPrice: totalSearchMaxPrice,
                },
                apiPayload: {
                    OnSale: false,
                    NewProduct: false,
                    MinPrice: totalSearchMinPrice,
                    MaxPrice: totalSearchMaxPrice,
                    Categories: [],
                    Suppliers: [supplierSelected],
                    Makes: [],
                    Years: [],
                    Models: [],
                    Components: [],
                    callForPricing: false,
                    isFacetTrimingCatalog: false,
                },
            };

            sessionStorage.setItem('searchedValue', supplierSelected);

            dispatch(updateFiltersRedux(updatedMultiSelectedFilters));
        }
    };

    return (
        <Box style={{ padding: 2, marginTop: '1rem' }}>
            <Box
                sx={{
                    padding: '5%',
                    border: '1px solid',
                    marginBottom: '10%',
                    borderRadius: '5px',
                }}
                id="selectedFiltersBox"
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {totalSelectedCount > 0 && (
                        <Typography sx={{ color: blueColor }}>
                            {translate('selected_filters_txt')}
                        </Typography>
                    )}
                </Box>
                {
                    <RenderItems
                        items={allSelectedItems}
                        showAllSelectedValues={showAllSelectedValues}
                        isPriceRangeMoved={isPriceRangeMoved}
                        minPrice={minPrice}
                        maxPrice={maxPrice}
                        displayedMin={displayedMin}
                        displayedMax={displayedMax}
                        handleResetPriceRange={handleResetPriceRange}
                        handleSelectedValueClick={handleSelectedValueClick}
                        handleToggleClick={handleToggleClick}
                        handleClearAllSelectedValues={
                            handleClearAllSelectedValues
                        }
                        handleApplyFiltersButton={handleApplyFiltersButton}
                        isApplyButtonVisible={isApplyButtonVisible}
                        selectedValuesChanged={selectedValuesChanged}
                        blueColor={blueColor}
                        translate={translate}
                        setIsApplyButtonVisible={setIsApplyButtonVisible}
                    />
                }
            </Box>

            <Box
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                {onSaleProducts && !isOnSaleDisabled && (
                    <Button
                        style={{
                            width: '33%',
                            borderRadius: 5,
                            marginRight: '10px',
                            textAlign: 'center',
                            border: '1px solid gray',
                            cursor: 'pointer',
                            backgroundColor: isOnSaleClicked
                                ? '#066FEF'
                                : 'white',
                            textTransform: 'none',
                            color: getColor(isOnSaleClicked),
                        }}
                        id="onSaleFilterBtn"
                        onClick={handleSaleClick}
                        startIcon={
                            isOnSaleClicked ? (
                                <FontAwesomeIcon
                                    icon={faRemove}
                                    size="xs"
                                    style={{
                                        fontSize: '10px',
                                        marginLeft: '10px',
                                    }}
                                />
                            ) : null
                        }
                    >
                        {translate(`On_Sale_FacetText`)}
                    </Button>
                )}
                {newProducts && !isNewDisabled && (
                    <Button
                        onClick={() => handleNewClick()}
                        style={{
                            width: '33%',
                            borderRadius: 5,
                            marginRight: '10px',
                            textAlign: 'center',
                            border: '1px solid gray',
                            cursor: 'pointer',
                            backgroundColor: isNewClicked ? '#066FEF' : 'white',
                            textTransform: 'none',
                            color: getColor(isNewClicked),
                        }}
                        id="newFilterBtn"
                        startIcon={
                            isNewClicked ? (
                                <FontAwesomeIcon
                                    icon={faRemove}
                                    size="xs"
                                    style={{
                                        fontSize: '10px',
                                        marginLeft: '5px',
                                    }}
                                />
                            ) : null
                        }
                    >
                        {translate(`CMSNewText`)}
                    </Button>
                )}
                {callForPricingProducts && (
                    <Button
                        onClick={() => handleCallForPricingClick()}
                        style={{
                            width: '70%',
                            borderRadius: 5,
                            textAlign: 'center',
                            border: '1px solid gray',
                            cursor: 'pointer',
                            textTransform: 'none',
                            backgroundColor: isCallForPricingClicked
                                ? '#066FEF'
                                : 'white',
                            color: getColor(isCallForPricingClicked),
                        }}
                        id="callForPricingFilterBtn"
                        startIcon={
                            isCallForPricingClicked ? (
                                <FontAwesomeIcon
                                    icon={faRemove}
                                    size="xs"
                                    style={{
                                        fontSize: '10px',
                                        marginLeft: '10px',
                                    }}
                                />
                            ) : null
                        }
                    >
                        {translate(`MissingPrice`)}
                    </Button>
                )}
            </Box>

            {minPrice != null &&
                maxPrice != null &&
                !isCallForPricingClicked &&
                !isInitialPageLoad && (
                    <Box
                        sx={{
                            backgroundColor: '#F5F5F5',
                            padding: '0px 10px 10px 10px',
                            marginTop: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '6px',
                                padding: ' 4px',
                                fontWeight: 'bold',
                                width: 'fit-content',
                                transition: 'all 0.3s ease-in-out',
                                borderRadius: '4px',
                                color: 'rgb(33, 37, 41)',
                            }}
                        >
                            <Typography id={'priceOption_Price'}>
                                {translate(`ltl_PriceFacetText`)}
                            </Typography>
                        </Box>

                        <Box
                            style={{
                                marginTop: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                width: 'auto',
                            }}
                        >
                            <Typography
                                style={{ textAlign: 'center', padding: '5px' }}
                            >{`${
                                totalSearchItems?.items[0]?.price
                                    ?.currencySymbol ?? '$'
                            }${minPrice}`}</Typography>
                            <Slider
                                value={price}
                                onChange={(
                                    event: Event,
                                    newValue: number | number[]
                                ) => {
                                    if (
                                        Array.isArray(newValue) &&
                                        (newValue[0] !== finalPrice[0] ||
                                            newValue[1] !== finalPrice[1])
                                    ) {
                                        setPrice(newValue);
                                    }
                                }}
                                onChangeCommitted={(
                                    event: Event,
                                    newValue: number | number[]
                                ) => {
                                    if (
                                        Array.isArray(newValue) &&
                                        (newValue[0] !== finalPrice[0] ||
                                            newValue[1] !== finalPrice[1])
                                    ) {
                                        setFinalPrice(newValue);
                                        setPriceRangeValues([
                                            minPrice,
                                            maxPrice,
                                        ]);
                                        setIsPriceRangeMoved(true);
                                        setIsApplyButtonVisible(true);
                                    }
                                }}
                                valueLabelDisplay="auto"
                                sx={{
                                    '& .MuiSlider-valueLabel': {
                                        backgroundColor: '#066FEF',
                                        color: 'white',
                                    },
                                }}
                                min={Number(minPrice)}
                                max={Number(maxPrice)}
                                style={{ flex: 1 }}
                                valueLabelFormat={(value: any) => `$${value}`}
                            />
                            <Typography
                                style={{
                                    textAlign: 'center',
                                    marginLeft: '5px',
                                }}
                            >
                                {`${
                                    totalSearchItems?.items[0]?.price
                                        ?.currencySymbol ?? '$'
                                }${maxPrice}`}
                            </Typography>
                        </Box>
                    </Box>
                )}
            <SubFiltersMenu
                totalItems={totalSearchItems}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                updatedFilterItems={updatedFilterItems}
                handleApplyFiltersButton={handleApplyFiltersButton}
                data={data}
                setData={setData}
                setSearchedValueLabel={setSearchedValueLabel}
                selectedValuesChanged={selectedValuesChanged}
                setIsApplyButtonVisible={setIsApplyButtonVisible}
                isApplyButtonVisible={isApplyButtonVisible}
                isInitialPageLoad={isInitialPageLoad}
            />
        </Box>
    );
};

const blueColor = 'rgb(14, 22, 100) !important';

export const findCodeByDisplayLabel = (
    rootLabel: string,
    leafLabel: string,
    allCategoriesWithChildren: { label: string; items: any[]; code: string }[]
): string | null => {
    for (const topCat of allCategoriesWithChildren) {
        if (topCat.label !== rootLabel) continue;
        for (const subCat of topCat.items || []) {
            if (subCat.label === leafLabel) return subCat.code;
            for (const leaf of subCat.items || []) {
                if (leaf.label === leafLabel) return leaf.code;
            }
        }
    }
    return null;
};

export const updateCategoryValues = (
    prevSelectedValues: SelectedValues,
    item: string,
    allCategoriesWithChildren?: { label: string; items: any[]; code: string }[]
): SelectedValues => {
    return Object.keys(prevSelectedValues).reduce((acc, category) => {
        const key = category as keyof SelectedValues;

        if (key === 'category' && item.includes(' > ')) {
            const itemSplitted = item.split(' > ');
            const rootLabel = itemSplitted[0];
            const leafLabel = itemSplitted[itemSplitted.length - 1];

            // Use tree to find the exact code to remove
            const codeToRemove = allCategoriesWithChildren
                ? findCodeByDisplayLabel(
                      rootLabel,
                      leafLabel,
                      allCategoriesWithChildren
                  )
                : null;

            if (codeToRemove) {
                acc[key] = prevSelectedValues[key].filter(
                    (value) => value !== codeToRemove
                );
            } else {
                // Fallback to pattern-based approach
                acc[key] = prevSelectedValues[key].filter((value) => {
                    const valueSplitted = value.split('_');
                    return !(
                        valueSplitted[0] === rootLabel &&
                        valueSplitted[valueSplitted.length - 1] === leafLabel
                    );
                });
            }
        } else {
            acc[key] = prevSelectedValues[key].filter(
                (value) => value !== item
            );
        }

        return acc;
    }, {} as SelectedValues);
};

export const getColor = (value: boolean) =>
    value ? 'white' : 'rgb(33, 37, 41)';

export const updateSelections = (
    updatedFilterItems: any,
    selectedValues: any,
    setData: Function,
    allMakes: string[],
    allYears: string[],
    allModels: string[],
    allComponents: string[]
) => {
    const selections = getFilteredData(
        updatedFilterItems?.refineCatalogs,
        selectedValues,
        allMakes,
        allYears,
        allModels,
        allComponents
    );
    setData(() => ({
        make: { value: selections.makes },
        model: { value: selections.models },
        year: { value: selections.years },
        component: { value: selections.components },
    }));
};

export default MultiSelectList;
